import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/sergey-listopad";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const sergeyListopad: CaseStudy = {
  slug: "sergey-listopad",
  title: "Сергей Листопад",
  cardTitle: "Разработка сайта для иллюзиониста Сергея Листопада",
  subtitle: "Шоу-бизнес и личный бренд",
  industry: { label: "Медиа и личный бренд", slug: "media" },
  eyebrow: "Лендинг · Личный бренд артиста",
  h1: "Разработка сайта для иллюзиониста Сергея Листопада",
  lead:
    "Собрали сайт личного бренда иллюзиониста Сергея Листопада: позиционирование STAND UP MAGIC, историю и медиа, шоу-программу и прямой сценарий заказа выступления.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт личного бренда" },

  seo: {
    primaryKeyword: "разработка сайта для артиста",
    secondaryKeywords: [
      "сайт личного бренда разработка",
      "сайт для иллюзиониста",
      "лендинг артиста",
      "сайт для шоу-программы",
    ],
    semanticEntities: [
      "STAND UP MAGIC",
      "история артиста",
      "мероприятия",
      "шоу-программа",
      "заказ выступления",
    ],
    excludedKeywords: [
      "фокусник на корпоратив",
      "заказать фокусника",
      "шоу программа цена",
      "иллюзионист Москва",
    ],
    title: "Сайт Сергея Листопада — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт личного бренда иллюзиониста с историей, шоу-программой, мероприятиями и прямым сценарием заказа выступления.",
    h1: "Разработка сайта для иллюзиониста Сергея Листопада",
    slug: "sergey-listopad",
    canonicalUrl: `${SITE_URL}/cases/sergey-listopad`,
    ogTitle: "Сайт личного бренда иллюзиониста Сергея Листопада",
    ogDescription:
      "Позиционирование STAND UP MAGIC, история артиста, шоу-программа и заказ выступления на одном лендинге.",
    ogImage: `${SITE_URL}${dir}/sergey-listopad-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка сайтов",
      to: routes.landing,
      note: "Лендинг личного бренда",
    },
    relatedIndustry: {
      label: "SEO-продвижение сайтов",
      to: routes.seo,
      note: "Видимость личного бренда",
    },
    relatedCases: [],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт личного бренда или артиста?",
    text: "Соберём позиционирование, историю, шоу-программу и сценарий заказа выступления.",
    button: "Обсудить сайт личного бренда",
  },

  client: {
    name: "Сергей Листопад",
    url: "https://sergeylistopad.ru/",
    city: "Россия",
    industry: "Шоу-бизнес и личный бренд",
  },
  year: "2024",
  services: [
    "Дизайн лендинга личного бренда",
    "Позиционирование STAND UP MAGIC",
    "Раздел истории и медиа",
    "Раздел шоу-программы",
    "Сценарий заказа выступления",
  ],
  platform: "Tilda",
  tags: [
    { label: "Лендинг", to: routes.landing },
    { label: "Разработка", to: routes.landing },
  ],
  facts: [
    { label: "Клиент", value: "Сергей Листопад" },
    { label: "Тип проекта", value: "Лендинг личного бренда артиста" },
    { label: "Платформа", value: "Tilda" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Позиционирование бренда как STAND UP MAGIC на первом экране",
      "Раздел истории и медиа с более чем 10-летней профессиональной деятельностью артиста",
      "Раздел мероприятий и шоу-программы",
      "CTA «Заказать выступление» на первом экране",
      "Прямой сценарий заказа выступления с контактами",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Сергей Листопад — иллюзионист с более чем 10-летней профессиональной деятельностью, позиционирующий себя в жанре STAND UP MAGIC. Сайт должен приводить организаторов мероприятий и компании, а не заказчиков разового фокуса, поэтому в основе — история, доверие и шоу-программа, а не прайс на услуги.",
        "Вторая часть задачи — прямой сценарий: посетитель должен быстро дойти от позиционирования к заказу выступления.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: сценическая подача без лишнего неона",
    paragraphs: [
      "Интерфейс собран на тёмной сценической подложке с реальным фото артиста — без магических sparkles и неона, не связанных с исходным стилем бренда.",
      "История и шоу-программа поданы крупно и коротко, чтобы посетитель считывал доверие и жанр за несколько секунд.",
    ],
    bullets: [
      "Тёмная сценическая подложка",
      "Реальное фото артиста в hero",
      "Короткая подача истории и шоу-программы",
    ],
  },

  identity: {
    fonts: [
      { name: "Prosto One", role: "Заголовки", sample: "STAND UP MAGIC" },
      { name: "Circe", role: "Текст и интерфейс", sample: "Заказать выступление" },
    ],
    colors: [
      { hex: "#141414", name: "Фон сцены" },
      { hex: "#C9A24B", name: "Акцент бренда" },
      { hex: "#F2EFEA", name: "Текст" },
    ],
  },

  cover: img(
    "sergey-listopad-case-cover",
    "Главная страница сайта личного бренда иллюзиониста Сергея Листопада",
    6,
  ),

  gallery: [
    img("sergey-listopad-story", "История и медиа Сергея Листопада", 6),
    img("sergey-listopad-show-program", "Шоу-программа Сергея Листопада", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: позиционирование, история, шоу-программа и заказ выступления в один сценарий.",
      "Смысловые карточки на мобильной версии идут горизонтальным скроллом.",
    ],
    images: [
      mobile("sergey-listopad-mobile-hero", "Мобильная главная сайта Сергея Листопада"),
      mobile("sergey-listopad-mobile-booking", "Мобильный сценарий заказа выступления"),
    ],
  },

  columnBlocks: [
    {
      id: "positioning",
      title: "Позиционирование",
      paragraphs: [
        "Hero и одно предложение о жанре сразу задают формат — STAND UP MAGIC, а не классическое шоу фокусов.",
      ],
      bullets: ["Формат STAND UP MAGIC", "CTA «Заказать выступление» на первом экране"],
    },
    {
      id: "story",
      title: "История и доверие",
      paragraphs: [
        "Медиа и история артиста поданы крупно и коротко — посетитель видит более 10 лет профессиональной деятельности.",
      ],
      bullets: ["Более 10 лет на сцене", "Медиа и упоминания"],
    },
    {
      id: "show",
      title: "Шоу-программа",
      paragraphs: [
        "Раздел шоу-программы описывает формат выступления и предложение для организатора мероприятия.",
      ],
      bullets: ["Формат выступления", "Раздел мероприятий"],
    },
    {
      id: "booking",
      title: "Заказ выступления",
      paragraphs: [
        "Форма и контакты собраны как финальный продуктовый сценарий — заказ выступления в один шаг.",
      ],
      bullets: ["Форма заказа выступления", "Контакты"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [{ label: "Разработка лендинга", to: routes.landing }],
  },
};
